import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import type { MatterColumn } from "hooks/useEmailsRestApi";
import type React from "react";
import type { FC } from "react";

type ParsedMatterViewProps = {
  matterColumn: MatterColumn;
  onRegisterAsFocusMatter?: () => void;
  isRegistering?: boolean;
};

// 単価をフォーマット（万円単位で表示）
const formatReward = (reward: string | null | undefined): string => {
  if (!reward || reward === "null") return "-";
  const num = parseInt(reward, 10);
  if (Number.isNaN(num)) return reward;
  // 値が1000以上の場合は万円単位に変換（例: 800000 → 80万円）
  if (num >= 1000) {
    return `${Math.round(num / 10000)}万円`;
  }
  return `${num}万円`;
};

// 業界カテゴリを日本語に変換
const formatIndustryCategory = (
  category: string | null | undefined,
): string => {
  if (!category) return "-";
  switch (category) {
    case "finance":
      return "金融";
    case "insurance":
      return "保険";
    case "government":
      return "官公庁";
    case "manufacturing":
      return "製造業";
    case "healthcare":
      return "医療";
    case "education":
      return "教育";
    case "retail":
      return "小売";
    case "startup":
      return "スタートアップ";
    default:
      return category;
  }
};

// リモート頻度をフォーマット
const formatRemoteFrequency = (
  frequency: string | null | undefined,
): string => {
  if (!frequency) return "-";
  switch (frequency) {
    case "full_remote":
      return "フルリモート";
    case "hybrid":
      return "ハイブリッド";
    case "onsite_only":
      return "常駐";
    default:
      return frequency;
  }
};

// 面談方法をフォーマット
const formatInterviewMethod = (method: string | null | undefined): string => {
  if (!method) return "-";
  switch (method) {
    case "web":
      return "Web面談";
    case "onsite":
      return "対面";
    case "combined":
      return "Web + 対面";
    default:
      return method;
  }
};

// Boolean値をフォーマット
const formatBoolean = (
  value: boolean | null | undefined,
  trueLabel = "可",
  falseLabel = "不可",
): string => {
  if (value === null || value === undefined) return "-";
  return value ? trueLabel : falseLabel;
};

// 文字列をフォーマット
const formatString = (
  value: string | null | undefined,
  suffix = "",
): string => {
  if (value === null || value === undefined || value === "" || value === "null")
    return "-";
  return value + suffix;
};

// 数値をフォーマット
const formatNumber = (
  value: number | string | null | undefined,
  suffix = "",
): string => {
  if (value === null || value === undefined || value === "null") return "-";
  if (typeof value === "string") {
    if (value === "") return "-";
    return value + suffix;
  }
  return value + suffix;
};

// 値が空かどうかをチェック
const isEmpty = (value: string): boolean => value === "-";

// 行コンポーネント
type InfoRowProps = {
  label: string;
  value: string;
  isHighlight?: boolean;
};

const InfoRow: FC<InfoRowProps> = ({ label, value, isHighlight = false }) => {
  const isEmptyValue = isEmpty(value);
  return (
    <div
      className={`flex border-b border-gray-100 py-2 ${isEmptyValue ? "text-gray-400" : ""}`}
    >
      <div
        className={`w-32 text-sm font-medium ${isEmptyValue ? "text-gray-400" : "text-gray-500"}`}
      >
        {label}
      </div>
      <div
        className={`flex-1 text-sm ${isEmptyValue ? "text-gray-400" : isHighlight ? "text-blue-600 font-medium" : "text-gray-900"}`}
      >
        {value}
      </div>
    </div>
  );
};

// Booleanバッジ行コンポーネント
type BooleanRowProps = {
  label: string;
  value: string;
  isTrue: boolean | null | undefined;
};

const BooleanRow: FC<BooleanRowProps> = ({ label, value, isTrue }) => {
  const isEmptyValue = isEmpty(value);
  return (
    <div
      className={`flex border-b border-gray-100 py-2 ${isEmptyValue ? "text-gray-400" : ""}`}
    >
      <div
        className={`w-32 text-sm font-medium ${isEmptyValue ? "text-gray-400" : "text-gray-500"}`}
      >
        {label}
      </div>
      <div className="flex-1 text-sm">
        {isEmptyValue ? (
          <span className="text-gray-400">{value}</span>
        ) : (
          <span
            className={`px-2 py-0.5 rounded text-xs ${
              isTrue
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {value}
          </span>
        )}
      </div>
    </div>
  );
};

// タグリスト行コンポーネント
type TagListRowProps = {
  label: string;
  values: string[] | null | undefined;
  colorClass?: string;
};

const TagListRow: FC<TagListRowProps> = ({
  label,
  values,
  colorClass = "bg-blue-100 text-blue-800",
}) => {
  const hasValues = values && values.length > 0;
  return (
    <div
      className={`flex border-b border-gray-100 py-2 ${!hasValues ? "text-gray-400" : ""}`}
    >
      <div
        className={`w-32 text-sm font-medium flex-shrink-0 ${!hasValues ? "text-gray-400" : "text-gray-500"}`}
      >
        {label}
      </div>
      <div className="flex-1 flex flex-wrap gap-1">
        {hasValues ? (
          values.map(value => (
            <span
              key={value}
              className={`px-2 py-0.5 rounded text-xs ${colorClass}`}
            >
              {value}
            </span>
          ))
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </div>
    </div>
  );
};

// テキストブロック行コンポーネント（案件詳細用）
type TextBlockRowProps = {
  label: string;
  value: string;
};

const TextBlockRow: FC<TextBlockRowProps> = ({ label, value }) => {
  const isEmptyValue = isEmpty(value);
  return (
    <div
      className={`flex border-b border-gray-100 py-2 ${isEmptyValue ? "text-gray-400" : ""}`}
    >
      <div
        className={`w-32 text-sm font-medium flex-shrink-0 ${isEmptyValue ? "text-gray-400" : "text-gray-500"}`}
      >
        {label}
      </div>
      <div
        className={`flex-1 text-sm whitespace-pre-line ${isEmptyValue ? "text-gray-400" : "text-gray-900"}`}
      >
        {value}
      </div>
    </div>
  );
};

// セクションコンポーネント
type SectionProps = {
  title: string;
  children: React.ReactNode;
};

const Section: FC<SectionProps> = ({ title, children }) => {
  return (
    <div className="mb-6">
      <h3 className="text-base font-semibold text-gray-800 mb-3 pb-2 border-b-2 border-gray-200">
        {title}
      </h3>
      <div>{children}</div>
    </div>
  );
};

export const ParsedMatterView: FC<ParsedMatterViewProps> = ({
  matterColumn,
  onRegisterAsFocusMatter,
  isRegistering,
}) => {
  // フォーマット済みの値を準備
  const detail = formatString(matterColumn.detail);
  const period = formatString(matterColumn.period);
  const reward = formatReward(matterColumn.reward);
  const workingHours = formatString(matterColumn.workingHours);
  const workingTimeWidth = formatString(matterColumn.workingTimeWidth);
  const station = formatString(matterColumn.station);
  const reqcuitmentRequired = formatNumber(
    matterColumn.reqcuitmentRequired,
    "名",
  );
  const industryCategory = formatIndustryCategory(
    matterColumn.industryCategory,
  );
  const remoteFrequency = formatRemoteFrequency(matterColumn.remoteFrequency);
  const interviewMethod = formatInterviewMethod(matterColumn.interviewMethod);
  const numberOfInterviews = formatNumber(
    matterColumn.numberOfInterviews,
    "回",
  );
  const paymentSite = formatNumber(matterColumn.paymentSite, "日");
  const otherCountries = formatBoolean(matterColumn.otherCountriesEnabled);
  const countryside = formatBoolean(matterColumn.isCountrysideEnabled);
  const freelance = formatBoolean(matterColumn.freelanceEnabled);
  const remarks = formatString(matterColumn.remarks);
  const depthLabel =
    matterColumn.depth != null ? `${matterColumn.depth}次請け` : "-";
  const maxHrDepthLabel =
    matterColumn.maxHrDepth != null
      ? matterColumn.maxHrDepth === 0
        ? "自社所属まで"
        : `${matterColumn.maxHrDepth - 1}社先まで`
      : "-";

  return (
    <div className="space-y-4">
      {/* CRM連携ボタン */}
      {onRegisterAsFocusMatter && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="success"
            size="sm"
            onClick={onRegisterAsFocusMatter}
            disabled={isRegistering}
          >
            {isRegistering ? <Loading size="xs" /> : "注力案件に登録"}
          </Button>
        </div>
      )}

      {/* 案件概要 */}
      <Section title="案件概要">
        <TextBlockRow label="案件詳細" value={detail} />
      </Section>

      {/* スキル・技術 */}
      <Section title="スキル・技術">
        <TagListRow
          label="開発環境"
          values={matterColumn.developmentEnvironment ?? undefined}
          colorClass="bg-blue-100 text-blue-800"
        />
        <TagListRow
          label="必須スキル"
          values={matterColumn.requiredSkills ?? undefined}
          colorClass="bg-red-100 text-red-800"
        />
        <TagListRow
          label="尚可スキル"
          values={matterColumn.niceToHaveSkills ?? undefined}
          colorClass="bg-yellow-100 text-yellow-800"
        />
      </Section>

      {/* 基本情報 */}
      <Section title="基本情報">
        <InfoRow label="開始時期" value={period} />
        <InfoRow label="単価" value={reward} isHighlight={!isEmpty(reward)} />
        <InfoRow label="就業時間" value={workingHours} />
        <InfoRow label="精算幅" value={workingTimeWidth} />
        <InfoRow label="最寄駅" value={station} />
        <InfoRow label="募集人数" value={reqcuitmentRequired} />
        <InfoRow label="業界" value={industryCategory} />
      </Section>

      {/* 勤務条件 */}
      <Section title="勤務条件">
        <InfoRow label="就業方法" value={remoteFrequency} />
        <BooleanRow
          label="外国籍"
          value={otherCountries}
          isTrue={matterColumn.otherCountriesEnabled}
        />
        <BooleanRow
          label="フリーランス"
          value={freelance}
          isTrue={matterColumn.freelanceEnabled}
        />
        <BooleanRow
          label="地方参画"
          value={countryside}
          isTrue={matterColumn.isCountrysideEnabled}
        />
      </Section>

      {/* 商流 */}
      <Section title="商流">
        <InfoRow label="商流の深さ" value={depthLabel} />
        <InfoRow label="人材深さ上限" value={maxHrDepthLabel} />
      </Section>

      {/* 面談・契約 */}
      <Section title="面談・契約">
        <InfoRow label="面談回数" value={numberOfInterviews} />
        <InfoRow label="面談方法" value={interviewMethod} />
        <InfoRow label="支払いサイト" value={paymentSite} />
      </Section>

      {/* 備考 */}
      <Section title="備考">
        <TextBlockRow label="備考" value={remarks} />
      </Section>
    </div>
  );
};
